import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Select, SearchBox, Segmented, Loading, ErrorState, EmptyState } from "./ui";
import {
  listAccountHeads, createAccountHead, renameAccountHead, removeAccountHead,
  listAllAccounts, setAccountHead,
} from "../api";

/**
 * Two halves of the same job: the list of account heads that the reporting
 * pages group by, and the chart of accounts mapped onto them.
 *
 * `accounts.name` holds the head as text - every reporting query already joins
 * on it - so renaming a head carries its accounts with it.
 */
export default function AccountHeads() {
  const [heads, setHeads] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");

  const [newHead, setNewHead] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");

  const [filter, setFilter] = useState("unmapped");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkHead, setBulkHead] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [h, a] = await Promise.all([listAccountHeads(), listAllAccounts()]);
      setHeads(h);
      setAccounts(a);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const unmappedCount = useMemo(
    () => accounts.filter((a) => !a.head).length, [accounts]
  );

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return accounts.filter((a) => {
      if (filter === "unmapped" && a.head) return false;
      if (filter === "mapped" && !a.head) return false;
      if (!needle) return true;
      return `${a.account_number} ${a.account_name || ""} ${a.type || ""}`
        .toLowerCase().includes(needle);
    });
  }, [accounts, filter, search]);

  /* ── heads ─────────────────────────────────────────────────────────── */
  const addHead = async () => {
    if (!newHead.trim()) return;
    setNotice("");
    try {
      const { head } = await createAccountHead(newHead.trim());
      setHeads((prev) => [...prev, head].sort((a, b) => a.name.localeCompare(b.name)));
      setNewHead("");
    } catch (err) { setNotice(err.message); }
  };

  const saveRename = async (head) => {
    if (!editName.trim() || editName.trim() === head.name) { setEditing(null); return; }
    setNotice("");
    try {
      const res = await renameAccountHead(head.id, editName.trim());
      setNotice(res.accountsMoved
        ? `Renamed — ${res.accountsMoved} account(s) moved with it.`
        : "Renamed.");
      setEditing(null);
      await load();
    } catch (err) { setNotice(err.message); }
  };

  const dropHead = async (head) => {
    if (!window.confirm(`Delete the head "${head.name}"?`)) return;
    setNotice("");
    try {
      await removeAccountHead(head.id);
      setHeads((prev) => prev.filter((h) => h.id !== head.id));
    } catch (err) { setNotice(err.message); }
  };

  /* ── mapping ───────────────────────────────────────────────────────── */
  const assign = async (numbers, head) => {
    setSaving(true);
    setNotice("");
    try {
      const res = await setAccountHead(numbers, head);
      setAccounts((prev) => prev.map((a) =>
        numbers.includes(a.account_number) ? { ...a, head: res.head } : a
      ));
      setHeads((prev) => prev.map((h) => ({
        ...h,
        accounts: h.name === res.head ? h.accounts + numbers.length : h.accounts,
      })));
      setSelected([]);
    } catch (err) { setNotice(err.message); }
    finally { setSaving(false); }
  };

  const toggle = (n) =>
    setSelected((prev) => prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]);
  const allShown = shown.length > 0 && shown.every((a) => selected.includes(a.account_number));

  if (loading) return <Loading label="Loading accounts…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const headOptions = heads.map((h) => ({ value: h.name, label: h.name }));

  return (
    <div className="stack">
      {notice && <div className="notice notice--warn">{notice}</div>}

      <Card title="Account heads" note="the groupings the P&L and Expenses pages report against" flush>
        <div className="dept-add">
          <div className="field" style={{ flex: 1, minWidth: 220 }}>
            <span className="field-label">New head</span>
            <input
              className="input"
              placeholder="e.g. Professional Services"
              value={newHead}
              onChange={(e) => setNewHead(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addHead()}
            />
          </div>
          <button className="btn btn--primary" onClick={addHead} disabled={!newHead.trim()}>
            Add head
          </button>
        </div>

        <div className="tbl-wrap tbl-wrap--flush">
          <table className="tbl tbl-zebra">
            <thead>
              <tr>
                <th className="col-flex">Head</th>
                <th className="num" style={{ width: 110 }}>Accounts</th>
                <th style={{ width: 1 }} />
              </tr>
            </thead>
            <tbody>
              {heads.map((h) => (
                <tr key={h.id}>
                  <td>
                    {editing === h.id ? (
                      <input
                        className="input"
                        value={editName}
                        autoFocus
                        onChange={(e) => setEditName(e.target.value)}
                        onBlur={() => saveRename(h)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveRename(h);
                          if (e.key === "Escape") setEditing(null);
                        }}
                      />
                    ) : (
                      <span
                        className="head-name"
                        onClick={() => { setEditing(h.id); setEditName(h.name); }}
                        title="Click to rename"
                      >
                        {h.name}
                      </span>
                    )}
                  </td>
                  <td className="num muted">{h.accounts}</td>
                  <td>
                    <button
                      className="btn btn--sm btn--danger"
                      onClick={() => dropHead(h)}
                      disabled={h.accounts > 0}
                      title={h.accounts > 0 ? "Move its accounts elsewhere first" : "Delete"}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card
        title="Accounts"
        note={`${accounts.length} in the chart · ${unmappedCount} not yet mapped`}
        actions={
          <>
            <Segmented
              value={filter}
              onChange={(v) => { setFilter(v); setSelected([]); }}
              options={[
                { value: "unmapped", label: `Unmapped${unmappedCount ? ` (${unmappedCount})` : ""}` },
                { value: "mapped", label: "Mapped" },
                { value: "all", label: "All" },
              ]}
            />
            <SearchBox value={search} onChange={setSearch} placeholder="Number, name or type…" />
          </>
        }
        flush
      >
        {selected.length > 0 && (
          <div className="edit-bar">
            <strong>{selected.length} selected</strong>
            <Select
              value={bulkHead}
              onChange={setBulkHead}
              options={[{ value: "", label: "Choose a head…" }, ...headOptions]}
              style={{ minWidth: 230 }}
            />
            <button
              className="btn btn--primary btn--sm"
              disabled={!bulkHead || saving}
              onClick={() => assign(selected, bulkHead)}
            >
              {saving ? "Saving…" : `Assign ${selected.length}`}
            </button>
            <button className="btn btn--sm btn--ghost" onClick={() => setSelected([])}>Clear</button>
          </div>
        )}

        {!shown.length ? (
          <EmptyState
            title={filter === "unmapped" ? "Every account has a head" : "Nothing matches"}
            hint={filter === "unmapped" ? undefined : "Try a different filter or search."}
          />
        ) : (
          <div className="tbl-wrap tbl-wrap--flush" style={{ maxHeight: 520 }}>
            <table className="tbl tbl-zebra">
              <thead>
                <tr>
                  <th style={{ width: 34 }}>
                    <input
                      className="cbx"
                      type="checkbox"
                      checked={allShown}
                      onChange={() => setSelected(allShown ? [] : shown.map((a) => a.account_number))}
                    />
                  </th>
                  <th style={{ width: 100 }}>Number</th>
                  <th className="col-flex">Account name</th>
                  <th style={{ width: 130 }}>Type</th>
                  <th className="num" style={{ width: 90 }}>Expenses</th>
                  <th style={{ width: 210 }}>Head</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((a) => (
                  <tr
                    key={a.account_number}
                    className={selected.includes(a.account_number) ? "is-selected" : ""}
                  >
                    <td style={{ textAlign: "center" }}>
                      <input
                        className="cbx"
                        type="checkbox"
                        checked={selected.includes(a.account_number)}
                        onChange={() => toggle(a.account_number)}
                      />
                    </td>
                    <td><span className="code-tag">{a.account_number}</span></td>
                    <td className="truncate" title={a.account_name}>
                      {a.account_name || <span className="dash">—</span>}
                    </td>
                    <td className="muted">{a.type || <span className="dash">—</span>}</td>
                    <td className="num muted">{a.expense_rows || <span className="dash">—</span>}</td>
                    <td>
                      <select
                        className={`mgr-select${a.head ? " is-overridden" : ""}`}
                        style={{ width: "100%", minWidth: 190 }}
                        value={a.head || ""}
                        onChange={(e) => assign([a.account_number], e.target.value)}
                      >
                        <option value="">— unmapped —</option>
                        {heads.map((h) => (
                          <option key={h.id} value={h.name}>{h.name}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
